import React, { useEffect, useState } from "react";
import {
  Input,
  Button,
  Alert,
  message,
  Checkbox,
  Row,
  Col,
  Popconfirm,
} from "antd";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { useForm, Controller, useWatch } from "react-hook-form";
import axios from "axios";
import ButtonLoader from "../ButtonLoader";

const schema = yup.object().shape({
  name: yup.string().required("Role name is required"),
});

const RoleForm = ({ editingRole, handleUpdateList }) => {
  const [loading, setLoading] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [errorsMessage, setErrorsMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isDataSet, setIsDataSet] = useState(false);

  const [createPermission, setCreatePermission] = useState(false);
  const [editPermission, setEditPermission] = useState(false);
  const [deletePermission, setDeletePermission] = useState(false);

  const [slugValue, setSlugValue] = useState("");
  const [permissionOptions, setPermissionOptions] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    context: { isEdit: !!editingRole },
  });

  const permissionsState = useWatch({ control, name: "permissions" });
  const currentPermissions = permissionsState || {};
  const allSelected = Object.values(currentPermissions).every((v) => v === "1");

  const grouped = permissionOptions.reduce((acc, perm) => {
    if (!acc[perm.group]) acc[perm.group] = [];
    acc[perm.group].push(perm);
    return acc;
  }, {});

  const nameValue = watch("name");

  useEffect(() => {
    if (nameValue && !editingRole) {
      const slug = nameValue
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, "") // remove special chars
        .replace(/\s+/g, "-") // replace spaces with dashes
        .replace(/-+/g, "-"); // remove duplicate dashes
      setValue("slug", slug);
      setSlugValue(slug);
    }
  }, [nameValue, setValue]);

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/roles/masters`
      );

      const permissions = response.data.data.permissions;

      setPermissionOptions(permissions);
      const initialPermissions = {};
      permissions.forEach((perm) => {
        initialPermissions[perm.value] = "-1";
      });
      setValue("permissions", initialPermissions);
      setLoading(false);
    } catch (err) {
      console.error(err.message);
      setLoading(false);
    }
  };

  const fetchRole = async (roleId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/roles/${roleId}`
      );
      const editFormData = {
        ...response.data.data,
        permissions: JSON.parse(response.data.data.permissions),
      };

      setSlugValue(editFormData.slug);
      setValue("permissions", editFormData.permissions);
      reset(editFormData);
      setLoading(false);
    } catch (err) {
      console.log(err);
    }
  };

  const handleSelectAll = (checked) => {
    const updated = {};
    permissionOptions.forEach((perm) => {
      updated[perm.value] = checked ? "1" : "-1";
    });
    setValue("permissions", updated, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleIndividualChange = (permValue, isChecked) => {
    setValue(
      "permissions",
      {
        ...currentPermissions,
        [permValue]: isChecked ? "1" : "-1",
      },
      { shouldValidate: true, shouldDirty: true }
    );
  };

  useEffect(() => {
    const fetchDataAndSetState = async () => {
      await fetchMasterData();
      setIsDataSet(true);
    };
    fetchDataAndSetState();
    const storedUser = JSON.parse(localStorage.getItem("userData"));
    const permissions = storedUser.permissions;
    setCreatePermission(parseInt(permissions["roles.create"]) === 1);
    setEditPermission(parseInt(permissions["roles.edit"]) === 1);
    setDeletePermission(parseInt(permissions["roles.delete"]) === 1);
  }, []);

  useEffect(() => {
    if (isDataSet && editingRole) {
      fetchRole(editingRole.id);
    }
  }, [isDataSet, editingRole]);

  const onSubmitRole = async (data) => {
    let differFormFields = {};
    if (editingRole !== null) {
      differFormFields = {
        id: editingRole.id,
      };
    }
    const finalFormData = {
      name: data.name.trim(),
      slug: data.slug.trim(),
      permissions: data.permissions,
      ...differFormFields,
    };
    setSubmitting(true);
    let newFormData = finalFormData;
    const url =
      editingRole === null
        ? `${process.env.REACT_APP_BASE_URL}/api/roles/create`
        : `${process.env.REACT_APP_BASE_URL}/api/roles/update`;

    try {
      if (editingRole === null) {
        await axios.post(url, newFormData);
        // Reset form after successful submission
        // reset(newFormData);
        message.success("Role created successfully");
      } else {
        const response = await axios.put(url, newFormData);
        const updated_row_count = response.data.data.updated_row_count;
        if (updated_row_count === 1) {
          // reset(data);
          message.success("Role updated successfully");
        }
      }
      setSubmitting(false);
      setShowErrors(false);
      handleUpdateList();
    } catch (error) {
      if (error.response && error.response.status > 300) {
        const errorData = error.response.data.message;
        // const errorField = error.response.data.field;
        console.log(errorData);
        // setShowErrors(false);
        setErrorsMessage(errorData);
        setShowErrors(true);
        setTimeout(() => {
          setShowErrors(false);
        }, 3000);

        setSubmitting(false);
      } else {
        console.error("Error:", error);
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/api/roles/${id}`
      );
      const updated_row_count = response.data.data.updated_row_count;
      if (updated_row_count === 1) {
        message.success("Role deleted");
        await handleUpdateList();
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <div>
      {loading ? (
        <ButtonLoader wrapperClass={`min-h-50`} />
      ) : (
        <>
          {showErrors && (
            <>
              <div className="col-sm-12 mb-3">
                <Alert message={errorsMessage} type="error" closeIcon={false} />
              </div>
            </>
          )}
          <form onSubmit={handleSubmit(onSubmitRole)}>
            <div className="mb-3">
              <label className="form-label">
                Role Name <span className="text-danger">*</span>
              </label>
              <Controller
                name="name"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.name && (
                <div className="text-danger small">{errors.name.message}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Slug</label>
              <Controller
                name="slug"
                control={control}
                render={({ field }) => (
                  <Input {...field} readOnly={true} value={slugValue} />
                )}
              />
              {errors.slug && (
                <div className="text-danger small">{errors.slug.message}</div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label">Permissions</label>

              <div className="mb-2">
                <Checkbox
                  checked={allSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                >
                  <strong>Select All</strong>
                </Checkbox>
              </div>

              {/* Render grouped checkboxes in horizontal grid */}
              {Object.keys(grouped).map((group) => (
                <div key={group} style={{ marginBottom: "1rem" }}>
                  <strong>{group}</strong>
                  <Row gutter={[16, 8]} style={{ marginTop: "0.5rem" }}>
                    {grouped[group].map((perm) => (
                      <Col key={perm.value} xs={24} sm={12} md={8} lg={4}>
                        <Checkbox
                          checked={currentPermissions[perm.value] === "1"}
                          onChange={(e) =>
                            handleIndividualChange(perm.value, e.target.checked)
                          }
                        >
                          {perm.label}
                        </Checkbox>
                      </Col>
                    ))}
                  </Row>
                </div>
              ))}
            </div>
            <div>
              {(createPermission || editPermission) && (
                <Button type="primary" className="m-1" htmlType="submit" disabled={submitting}>
                  {!submitting ? (
                    editingRole ? (
                      "Update Role"
                    ) : (
                      "Add Role"
                    )
                  ) : (
                    <ButtonLoader text="Submitting..." />
                  )}
                </Button>
              )}

              {editingRole && deletePermission && (
                <Popconfirm
                  title="Are you sure to delete this role?"
                  onConfirm={() => handleDelete(editingRole.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    danger
                    className="m-1"
                    disabled={editingRole.is_default === 1}
                  >
                    Delete Role
                  </Button>
                </Popconfirm>
              )}
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default RoleForm;
